/**
 * Universal Links utility functions for iOS app integration
 */

// Check if the user is on iOS
export const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

// Check if the user is on iOS Safari
export const isIOSSafari = () => {
  const ua = navigator.userAgent;
  return isIOS() && /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|mercury/.test(ua);
};

// Check if we're in an in-app browser (Instagram, Snapchat, etc.)
export const isInAppBrowser = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // List of known in-app browser user agent strings
  const inAppBrowsers = [
    'Instagram',
    'FBAN',      // Facebook App
    'FBAV',      // Facebook App
    'Snapchat',
    'TikTok',
    'Twitter',
    'LinkedIn',
    'WhatsApp',
    'Telegram',
    'Pinterest',
    'Reddit',
    'Line/',     // Line messenger
    'Messenger', // Facebook Messenger
  ];
  
  return inAppBrowsers.some(browser => userAgent.includes(browser));
};

// Check if we're in a standalone web app (added to home screen)
export const isStandalone = () => {
  return window.navigator.standalone === true || 
         window.matchMedia('(display-mode: standalone)').matches;
};

// Check if the native app is likely installed
export const checkIfAppInstalled = (timeout = 2000) => {
  return new Promise((resolve) => {
    if (!isIOS()) {
      resolve(false);
      return;
    }

    let resolved = false;
    
    const timer = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        resolve(false);
      }
    }, timeout);

    // Create a hidden iframe to try the app URL
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = 'mymenucart://';
    
    document.body.appendChild(iframe);
    
    // If the page becomes hidden/blurred, the app likely opened
    const handleVisibilityChange = () => {
      if ((document.hidden || document.webkitHidden) && !resolved) {
        resolved = true;
        clearTimeout(timer);
        cleanup();
        resolve(true);
      }
    };

    const handleBlur = () => {
      if (!resolved) {
        resolved = true;
        clearTimeout(timer);
        cleanup();
        resolve(true);
      }
    };

    const cleanup = () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('webkitvisibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('pagehide', handleBlur);
      if (iframe && iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('webkitvisibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('pagehide', handleBlur);

    // Cleanup after timeout
    setTimeout(cleanup, timeout + 100);
  });
};

// Generate app store link with fallback
export const getAppStoreLink = (fallbackUrl = window.location.href) => {
  const appStoreId = '6739787015'; // Your actual App Store ID from the meta tag
  return `https://apps.apple.com/app/id${appStoreId}`;
};

// Smart redirect function that tries app first, then app store
export const smartRedirect = async (appUrl, fallbackUrl = null) => {
  if (!isIOS()) {
    // Not iOS, stay on web
    return false;
  }

  if (isStandalone()) {
    // Already in standalone mode, no need to redirect
    return false;
  }

  try {
    const appInstalled = await checkIfAppInstalled();
    
    if (!appInstalled && fallbackUrl) {
      // App not installed, redirect to App Store
      window.location.href = getAppStoreLink(fallbackUrl);
      return true;
    }
    
    return appInstalled;
  } catch (error) {
    console.error('Error checking app installation:', error);
    return false;
  }
};

// Create a banner component data
export const getSmartBannerData = (currentPath) => {
  const baseUrl = window.location.origin;
  const currentUrl = `${baseUrl}${currentPath}`;
  
  return {
    title: 'MyMenuCart',
    author: 'MyMenuCart',
    price: 'FREE',
    appStoreLanguage: 'us',
    inAppStore: getAppStoreLink(currentUrl),
    inGooglePlay: '', // Add if you have Android app
    icon: '/myMenuCartIcon.png',
    button: 'OPEN',
    store: {
      ios: 'On the App Store',
      android: 'In Google Play'
    }
  };
};

// Generate deep link for sharing (community invite link)
export const generateShareLink = (communityId, webFallback = true) => {
  const webUrl = `${window.location.origin}/share/${communityId}`;
  const appUrl = `mymenucart://share/${communityId}`;
  
  if (webFallback) {
    return webUrl; // Universal link that works for both web and app
  }
  
  return appUrl; // Deep link directly to app
};

// Universal deep link opener - works across all browsers and platforms
export const openDeepLink = (deepLinkUrl, fallbackUrl = null, timeout = 3000) => {
  return new Promise((resolve) => {
    let opened = false;
    let startTime = Date.now();
    let timer = null;

    // Function to handle fallback
    const handleFallback = () => {
      if (!opened && fallbackUrl) {
        window.location.href = fallbackUrl;
        resolve(false);
      }
    };

    // Listen for page visibility changes
    const handleVisibilityChange = () => {
      if (document.hidden || document.webkitHidden) {
        opened = true;
        clearTimeout(timer);
        cleanup();
        resolve(true);
      }
    };

    // Listen for blur events
    const handleBlur = () => {
      opened = true;
      clearTimeout(timer);
      cleanup();
      resolve(true);
    };

    // Listen for focus back (app didn't open)
    const handleFocus = () => {
      const elapsed = Date.now() - startTime;
      // If we get focus back quickly, the app likely didn't open
      if (elapsed < 1000) {
        console.log('App likely not installed - got focus back quickly');
      }
    };

    const cleanup = () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('webkitvisibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('pagehide', handleBlur);
      window.removeEventListener('focus', handleFocus);
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('webkitvisibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('pagehide', handleBlur);
    window.addEventListener('focus', handleFocus);

    // Set timeout for fallback
    timer = setTimeout(() => {
      cleanup();
      handleFallback();
    }, timeout);

    // iOS-specific handling: Use window.location directly (most reliable on iOS)
    if (isIOS()) {
      // On iOS, direct window.location is most reliable
      window.location.href = deepLinkUrl;
    } else {
      // For other platforms, try multiple methods
      
      // Method 1: Direct window.location
      window.location.href = deepLinkUrl;
      
      // Method 2: Hidden iframe (works in some browsers and in-app browsers)
      try {
        const iframe = document.createElement('iframe');
        iframe.style.border = 'none';
        iframe.style.width = '1px';
        iframe.style.height = '1px';
        iframe.style.position = 'absolute';
        iframe.style.top = '-1000px';
        iframe.src = deepLinkUrl;
        document.body.appendChild(iframe);
        
        setTimeout(() => {
          if (iframe.parentNode) {
            document.body.removeChild(iframe);
          }
        }, timeout + 500);
      } catch (e) {
        console.log('iframe method failed:', e);
      }
    }
  });
};

// Generate deep link for sharing an individual recipe
export const generateRecipeShareLink = (recipeId, webFallback = true) => {
  const webUrl = `${window.location.origin}/recipe/${recipeId}`;
  const appUrl = `mymenucart://recipe/${recipeId}`;
  
  if (webFallback) {
    return webUrl; // Universal link that works for both web and app
  }
  
  return appUrl; // Deep link directly to app
};
